import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { MatDialog } from '@angular/material/dialog';
import { catchError } from 'rxjs/operators';
import { of } from 'rxjs';
import { appVersion } from '../version.config';
import { WhatsNewModalComponent } from '../shared/components/whats-new-modal/whats-new-modal.component';
import { SecureSessionService } from './secure-session.service';

@Injectable({ providedIn: 'root' })
export class AppVersionService {
  // Stored in sessionStorage so it survives the reload but clears on tab close.
  private readonly RELOAD_GUARD_KEY = 'app_reload_guard';
  private readonly WHATS_NEW_KEY = `whatsNewAcknowledged_${appVersion.version}`;
  private lastCheck = 0;

  constructor(private http: HttpClient, private dialog: MatDialog, private session: SecureSessionService) {}

  /** Call once from AppComponent.ngOnInit. */
  init(): void {
    this.fetchAndCompare();
    this.showWhatsNewIfNeeded();
  }

  /** Call on every NavigationEnd; self-throttled to once per 60 s. */
  checkOnNavigation(): void {
    const now = Date.now();
    if (now - this.lastCheck < 60_000) return;
    this.lastCheck = now;
    this.fetchAndCompare();
    // Re-check here so the popup fires after a post-login redirect.
    this.showWhatsNewIfNeeded();
  }

  private fetchAndCompare(): void {
    this.http
      .get<{ version: string }>(`/assets/version.json?t=${Date.now()}`, {
        headers: { 'Cache-Control': 'no-cache, no-store' }
      })
      .pipe(catchError(() => of(null)))
      .subscribe(res => {
        if (!res?.version || res.version === appVersion.version) return;
        // Skip if we already triggered a reload for this exact version (loop guard).
        if (sessionStorage.getItem(this.RELOAD_GUARD_KEY) === res.version) return;
        sessionStorage.setItem(this.RELOAD_GUARD_KEY, res.version);
        window.location.reload();
      });
  }

  private showWhatsNewIfNeeded(): void {
    if (!appVersion.showWhatsNew) return;
    if (localStorage.getItem(this.WHATS_NEW_KEY)) return;
    if (!this.isSuperAdmin()) return;

    // Delay so the app is fully painted before the dialog opens.
    setTimeout(() => {
      this.dialog
        .open(WhatsNewModalComponent, {
          data: { version: appVersion.version, features: appVersion.features },
          panelClass: ['sms-dialog-pane', 'sms-dialog-sm'],
          disableClose: false,
          autoFocus: false
        })
        .afterClosed()
        .subscribe(() => localStorage.setItem(this.WHATS_NEW_KEY, 'true'));
    }, 1200);
  }

  private isSuperAdmin(): boolean {
    try {
      const session = JSON.parse(this.session.getSession());
      const roles: string[] = Array.isArray(session?.[0]?.roles) ? session[0].roles : [];
      return roles.includes('super_admin');
    } catch {
      return false;
    }
  }
}
